/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as React from "react";
import { CompletedEnhanceSpeechTrack } from "components/EnhancedSpeech/Utilities";
import PlayPauseButton from "components/PlayPauseButton";
import styled from "styled-components";
import EnhancedSwitch from "components/shared/EnhancedSwitch";
import useBasicAudioPlayer from "hooks/useBasicAudioPlayer";
import { ModelVersion } from "components/EnhancedSpeech/useEnhanceSpeech/ModelVersion";
import { useCurrentUser } from "actions/CurrentUserActions";
import "stylesheets/spectrum.scss";

const PreviewContainer = styled.div`
  height: 70px;
  width: 320px;
  padding-left: 10px;
  padding-right: 10px;
  border: solid 3px var(--spectrum-global-color-static-gray-200);
  border-radius: 45px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  column-gap: 16px;
`;

const PreviewColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-grow: 1;
  width: 0%;
  row-gap: 0px;
`;

const PreviewLabel = styled.div.attrs({
  className: "spectrum-Switch-label",
})`
  text-align: start;
  font-size: 16px;
  line-height: 20px;
  font-weight: normal;
  color: var(--spectrum-global-color-static-gray-800);
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  width: 100%;
  margin-top: 0;
`;

export const SampleBasicEnhancedTrackPlayer: React.FC = () => {
  const { currentUser } = useCurrentUser();
  const enhanceSpeechV2Enabled = Boolean(
    currentUser?.enhanceSpeechV2FreeEnabled
  );

  const track: CompletedEnhanceSpeechTrack = enhanceSpeechV2Enabled
    ? {
        id: "",
        trackName: "Sample-V2.mp3",
        originalAudioUrl: `${process.env.CDN_ROOT}/enhance/sample_unenhanced_v2.mp3`,
        enhanceSpeechAudioUrl: `${process.env.CDN_ROOT}/enhance/sample_enhanced_v2.mp3`,
        mergedMediaUrl: "enhance sample preview doesn't use merged track",
        errorType: null,
        startedProcessingAt: null,
        finishedProcessingAt: "whenever",
        duration: null,
        createdAt: "",
        limits: null,
        isVideo: false,
        modelVersion: ModelVersion.v2,
      }
    : {
        id: "",
        trackName: "Sample.mp3",
        originalAudioUrl: `${process.env.CDN_ROOT}/enhance/sample_unenhanced_speech.wav`,
        enhanceSpeechAudioUrl: `${process.env.CDN_ROOT}/enhance/sample_enhanced_speech.wav`,
        mergedMediaUrl: "enhance sample preview doesn't use merged track",
        errorType: null,
        startedProcessingAt: null,
        finishedProcessingAt: "whenever",
        duration: null,
        createdAt: "",
        limits: null,
        isVideo: false,
        modelVersion: ModelVersion.v1,
      };

  const {
    enhanced,
    isLoaded,
    isPlaying,
    togglePlayback,
    toggleEnhance,
    displayName,
  } = useBasicAudioPlayer({
    track,
    startEnhanced: false,
  });

  return (
    <PreviewContainer data-testid="enhance-speech-preview">
      <PlayPauseButton
        isPlaying={isPlaying}
        trackDescription={displayName}
        buttonDiameterPx={52}
        onClick={togglePlayback}
        isLoaded={isLoaded}
      />
      <PreviewColumn>
        <EnhancedSwitch
          checked={enhanced ? true : undefined}
          onClick={toggleEnhance}
        />
      </PreviewColumn>
    </PreviewContainer>
  );
};
